import { escapeHTML } from '../../utils/escape.js';
import { formatPrice } from '../../utils/formatters.js';

/**
 * Tarjeta condensada para categorías tipo "salsas" / "guarniciones": en vez de
 * una card por producto, AGRUPA los productos por PRECIO en una sola línea cada
 * grupo, para condensar y diferenciarlas del resto de la carta.
 *
 * Reglas:
 *  - Se agrupa por precio; dentro del grupo el orden es por `position` (orden Fudo).
 *  - Los grupos se ordenan por precio ASCENDENTE (los "sin costo" primero).
 *  - `stripWord` (ej. "salsa") saca esa palabra inicial de cada nombre — la
 *    categoría ya lo dice ("SALSA MIXTA" → "mixta"). Para guarniciones no se saca
 *    nada (no hay palabra redundante).
 *  - Precio 0 → "sin costo" en itálica (queda mejor que "$0").
 *  - Estilo gris sutil (bg gris claro + texto gris un tono más oscuro), theme-aware.
 *
 * @param {{ title: string, products: Array<{ name: string, price: number, position: number }>, stripWord?: string|null }} params
 * @returns {string} HTML de la tarjeta.
 */

/**
 * Agrupa productos por precio. Devuelve `[{ price, names }]` ordenado por precio asc;
 * dentro de cada grupo los nombres quedan en orden de `position`.
 */
function groupByPrice(products, stripWord) {
    const stripRe = stripWord ? new RegExp(`^${stripWord}\\s+`, 'i') : null;
    const byPrice = new Map();
    for (const p of [...products].sort((a, b) => a.position - b.position)) {
        const name = (stripRe ? p.name.replace(stripRe, '') : p.name).trim();
        if (!byPrice.has(p.price)) byPrice.set(p.price, []);
        byPrice.get(p.price).push(name);
    }
    return [...byPrice.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([price, names]) => ({ price, names }));
}

export const PriceGroupCard = ({ title, products, stripWord = null }) => {
    const groups = groupByPrice(products, stripWord);

    const lines = groups
        .map(({ price, names }) => {
            const namesHtml = names.map((n) => escapeHTML(n)).join(' - ');
            const priceHtml =
                price > 0
                    ? `<span class="shrink-0 font-black">${formatPrice(price)}</span>`
                    : `<span class="shrink-0 italic opacity-80">sin costo</span>`;
            return `
                <div class="flex items-baseline justify-between gap-4">
                    <span class="lowercase">${namesHtml}</span>
                    ${priceHtml}
                </div>`;
        })
        .join('');

    return `
        <div class="rounded-2xl bg-black/[0.05] dark:bg-white/[0.06] border border-border-subtle px-5 py-4 mb-6">
            <span class="block text-[9px] font-black uppercase tracking-[0.2em] text-text-muted mb-3">${escapeHTML(title)}</span>
            <div class="space-y-2 text-[13px] leading-snug font-medium text-text-secondary">
                ${lines}
            </div>
        </div>
    `;
};
